import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { updateUserProfile } from "../services/userService";
import { loginWithSpotify } from "../services/spotifyService";

export default function OnboardingScreen({ onComplete }) {
  const { user, profile, refreshProfile } = useAuth();
  const [step, setStep] = useState(1);
  const [displayName, setDisplayName] = useState("");
  const [bio, setBio] = useState("");
  const [privacy, setPrivacy] = useState("public");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // Signed in with Spotify — already linked, so there is no second step
  const spotifyLinked = !!profile?.connectedAccounts?.spotify?.connected;

  useEffect(() => {
    if (profile) {
      setDisplayName(profile.displayName || "");
      setBio(profile.bio || "");
      setPrivacy(profile.privacy || "public");
    }
  }, [profile]);

  const handleStep1 = async (e) => {
    e.preventDefault();
    if (!displayName.trim()) {
      setError("Display name is required");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await updateUserProfile(user.uid, {
        displayName: displayName.trim(), bio: bio.trim(), privacy,
        ...(spotifyLinked ? { onboardingComplete: true } : {}),
      });
      await refreshProfile();
      if (spotifyLinked) {
        onComplete();
        return;
      }
      setStep(2);
    } catch (err) {
      setError(err.message || "Could not save profile");
    } finally {
      setLoading(false);
    }
  };

  const handleSkipSpotify = async () => {
    setLoading(true);
    try {
      await updateUserProfile(user.uid, { onboardingComplete: true });
      await refreshProfile();
      onComplete();
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleConnectSpotify = () => {
    loginWithSpotify("onboarding");
  };

  return (
    <div style={{
      background: "#0A0A0A", minHeight: "100vh",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "20px", fontFamily: "'DM Sans', system-ui, sans-serif",
      position: "relative", overflow: "hidden",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Serif+Display:ital@0;1&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 0; }
        .ob-glow { position: absolute; inset: 0; background: radial-gradient(ellipse at 50% 40%, rgba(29,185,84,0.08) 0%, transparent 70%); pointer-events: none; }
        .ob-card { width: 100%; max-width: 420px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 24px; padding: 36px 28px; position: relative; z-index: 1; }
        .ob-logo { font-family: 'DM Serif Display', serif; font-size: 36px; color: #fff; letter-spacing: -2px; text-align: center; margin-bottom: 8px; }
        .ob-title { font-family: 'DM Serif Display', serif; font-size: 28px; color: #fff; letter-spacing: -1px; text-align: center; margin-bottom: 8px; }
        .ob-sub { font-size: 14px; color: rgba(255,255,255,0.5); text-align: center; margin-bottom: 28px; line-height: 1.5; }
        .ob-steps { display: flex; gap: 8px; justify-content: center; margin-bottom: 28px; }
        .ob-step-dot { width: 8px; height: 8px; border-radius: 50%; background: rgba(255,255,255,0.15); transition: all 0.2s; }
        .ob-step-dot.active { background: #1DB954; width: 24px; border-radius: 100px; }
        .ob-tag { font-size: 12px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: #1DB954; margin-bottom: 16px; text-align: center; }
        .ob-input { width: 100%; padding: 14px 16px; border-radius: 12px; background: rgba(255,255,255,0.06); border: 1.5px solid transparent; font-family: 'DM Sans', sans-serif; font-size: 15px; color: #fff; outline: none; margin-bottom: 12px; transition: all 0.15s; }
        .ob-input:focus { border-color: rgba(255,255,255,0.3); background: rgba(255,255,255,0.08); }
        .ob-input::placeholder { color: rgba(255,255,255,0.3); }
        .ob-textarea { resize: none; min-height: 80px; }
        .ob-privacy { display: flex; gap: 8px; margin-bottom: 16px; }
        .ob-privacy-btn { flex: 1; padding: 10px; border-radius: 100px; border: 1.5px solid rgba(255,255,255,0.15); background: none; font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; cursor: pointer; color: rgba(255,255,255,0.5); transition: all 0.2s; }
        .ob-privacy-btn.active { background: #ffffff; color: #0A0A0A; border-color: #ffffff; }
        .ob-btn { width: 100%; padding: 14px 32px; border-radius: 100px; border: none; font-family: 'DM Sans', sans-serif; font-size: 15px; font-weight: 600; cursor: pointer; transition: all 0.2s; margin-bottom: 10px; display: flex; align-items: center; justify-content: center; gap: 10px; }
        .ob-btn:active { transform: scale(0.97); }
        .ob-btn-primary { background: #ffffff; color: #0A0A0A; font-weight: 700; }
        .ob-btn-primary:hover { background: rgba(255,255,255,0.9); transform: translateY(-1px); }
        .ob-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
        .ob-btn-spotify { background: #1DB954; color: #ffffff; font-weight: 700; }
        .ob-btn-spotify:hover { background: #1aa34a; }
        .ob-btn-ghost { background: none; color: rgba(255,255,255,0.5); border: none; font-size: 13px; font-weight: 500; }
        .ob-btn-ghost:hover { color: rgba(255,255,255,0.8); }
        .ob-error { background: rgba(255,107,107,0.1); border: 1px solid rgba(255,107,107,0.3); border-radius: 12px; padding: 12px 14px; font-size: 13px; color: #ff6b6b; margin-bottom: 16px; line-height: 1.5; }
        .ob-spotify-card { background: rgba(29,185,84,0.08); border: 1px solid rgba(29,185,84,0.2); border-radius: 20px; padding: 24px; text-align: center; margin-bottom: 20px; }
        .ob-spotify-icon { font-size: 48px; margin-bottom: 12px; }
        .ob-spotify-desc { font-size: 13px; color: rgba(255,255,255,0.5); line-height: 1.6; margin-bottom: 0; }
      `}</style>

      <div className="ob-glow" />

      <div className="ob-card">
        <div className="ob-logo">tapped</div>
        {!spotifyLinked && (
          <div className="ob-steps">
            <div className={`ob-step-dot ${step === 1 ? "active" : ""}`} />
            <div className={`ob-step-dot ${step === 2 ? "active" : ""}`} />
          </div>
        )}

        {step === 1 ? (
          <>
            <div className="ob-tag">{spotifyLinked ? "Spotify connected" : "Step 1 of 2"}</div>
            <div className="ob-title">Set up your profile</div>
            <div className="ob-sub">Tell people who you are at the venue</div>

            {error && <div className="ob-error">{error}</div>}

            <form onSubmit={handleStep1}>
              <input
                className="ob-input"
                placeholder="Display name *"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
              />
              <textarea
                className="ob-input ob-textarea"
                placeholder="Bio — optional, share your music taste"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
              />
              <div className="ob-privacy">
                <button type="button" className={`ob-privacy-btn ${privacy === "public" ? "active" : ""}`} onClick={() => setPrivacy("public")}>
                  🌍 Public
                </button>
                <button type="button" className={`ob-privacy-btn ${privacy === "private" ? "active" : ""}`} onClick={() => setPrivacy("private")}>
                  🔒 Private
                </button>
              </div>
              <button type="submit" className="ob-btn ob-btn-primary" disabled={loading}>
                {loading ? "Saving..." : "Continue"}
              </button>
            </form>
          </>
        ) : (
          <>
            <div className="ob-tag">Step 2 of 2</div>
            <div className="ob-title">Connect Spotify</div>
            <div className="ob-sub">Link your account to request songs and sync your taste</div>

            {error && <div className="ob-error">{error}</div>}

            <div className="ob-spotify-card">
              <div className="ob-spotify-icon">🎵</div>
              <p className="ob-spotify-desc">
                Connect Spotify to request songs at venues, see what's playing, and share your music profile with others.
              </p>
            </div>

            <button className="ob-btn ob-btn-spotify" onClick={handleConnectSpotify} disabled={loading}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02z"/></svg>
              Connect Spotify
            </button>
            <button className="ob-btn ob-btn-ghost" onClick={handleSkipSpotify} disabled={loading}>
              Skip for now
            </button>
          </>
        )}
      </div>
    </div>
  );
}
